import { useState, useEffect } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { identityService } from '../../../shared/api'
import { Button } from '../../../components'
import { useToast } from '../../../components/ToastProvider'
import { useTenantBranding } from '../../../contexts/TenantContext.jsx'
import { Mail, CheckCircle, Clock, AlertTriangle } from 'lucide-react'

/**
 * Which contract types automatically email the customer a copy of their signed field ticket.
 *
 * ONE SWITCH PER CONTRACT TYPE, and the list is not hardcoded here. The keys, their labels, and
 * which ones actually fire all come from the server, because the vocabulary is derived from
 * `shared/contract_types.py` — so a contract type added in Python appears here on its own rather
 * than waiting for somebody to remember this file.
 *
 * THE HONEST PART: only Time & Material is wired. Everything else produces a `time_backup` ticket,
 * which nobody counter-signs, so there is no second signature to fire on and it would have to send
 * at generation instead. Rather than hide those switches or grey them out with no explanation, they
 * are shown, they save, and they are labelled "saved, not sending yet" with the reason. A control
 * that silently does nothing is worse than one that says what it is waiting for.
 */
// The tenant this page is editing. NOT `user.tenant_id` — that field does not exist. The login
// response is `UserResponse`, which declares no tenant id at all (schemas/auth.py), so Pydantic
// drops it and the browser never sees one; the tenant id lives only inside the signed JWT. Reading
// it off `user` yielded `undefined` for EVERY role, which then got spelled into the request URL as
// the literal string "undefined" and came back 403.
//
// `'master'` is not a tenant either: on the platform-admin host TenantContext sets that literal
// (TenantContext.jsx:44), and sending it would 403 the same way. These cards edit ONE company's
// settings, so on a host that names no company there is nothing for them to edit.
function useEditableTenantId() {
  const branding = useTenantBranding()
  const id = branding?.tenantId
  return id && id !== 'master' ? id : null
}

export default function FieldTicketEmailSettings() {
  const { isAdmin } = useAuth()
  const toast = useToast()
  const tenantId = useEditableTenantId()

  const [settings, setSettings] = useState({})
  const [labels, setLabels] = useState({})
  const [wired, setWired] = useState({})
  const [unwiredReason, setUnwiredReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const canEdit = isAdmin && isAdmin()

  useEffect(() => {
    if (!tenantId) {
      // NOT a silent empty card. Rendering nothing reads as 'this is switched off',
      // which is a different and more misleading statement than 'we cannot tell'.
      setLoadError('These settings belong to one company. Open this page on that '
                   + "company's own site to change them.")
      setLoading(false)
      return
    }
    let cancelled = false
    identityService.getFieldTicketEmailSettings(tenantId)
      .then(data => {
        if (cancelled) return
        setSettings(data.settings || {})
        setLabels(data.labels || {})
        setWired(data.wired || {})
        setUnwiredReason(data.unwired_reason || '')
      })
      .catch(err => {
        if (cancelled) return
        // Named, not swallowed. A settings card that renders empty on a failed load looks like
        // "nothing is switched on", which is a different and more dangerous statement.
        console.error('Failed to load field ticket email settings', err)
        setLoadError('Could not load these settings. Refresh to try again.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [tenantId])

  const toggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }))
    setDirty(true)
  }

  const save = async () => {
    if (!tenantId) {
      // Belt and braces: the load effect already refuses, but the two guards were
      // never the same guard, which is how a URL containing "undefined" reached
      // the server at all.
      toast.error('This page is not showing one company, so there is nothing to save to.')
      return
    }
    setSaving(true)
    try {
      const res = await identityService.updateFieldTicketEmailSettings(tenantId, settings)
      setSettings(res.settings || settings)
      setDirty(false)
      toast.success('Field ticket email settings saved.')
    } catch (err) {
      console.error('Failed to save field ticket email settings', err)
      toast.error(err?.response?.data?.detail || 'Could not save those settings.')
    } finally {
      setSaving(false)
    }
  }

  const keys = Object.keys(labels)

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Mail size={18} className="text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Field ticket emails</h2>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        When a field ticket is signed by both parties, email a copy of the signed ticket to the
        site contact automatically. Off for every contract type until you turn it on.
      </p>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      {loadError && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{loadError}</span>
        </div>
      )}

      {!loading && !loadError && (
        <>
          <div className="space-y-2">
            {keys.map(key => {
              const on = settings[key] === true
              const isWired = wired[key] === true
              return (
                <label
                  key={key}
                  className={`flex items-start gap-3 rounded-md border p-3 ${
                    canEdit ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                  } ${on ? 'border-gray-300 bg-white' : 'border-gray-200 bg-gray-50'}`}
                >
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4"
                    checked={on}
                    disabled={!canEdit || saving}
                    onChange={() => toggle(key)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{labels[key]}</span>
                      {on && isWired && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
                          <CheckCircle size={12} /> sending
                        </span>
                      )}
                      {on && !isWired && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          <Clock size={12} /> saved, not sending yet
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-600">
                      {isWired
                        ? 'Sends when the second signature lands — the customer’s and yours.'
                        : unwiredReason}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>

          <p className="mt-3 text-xs text-gray-500">
            A ticket is only sent once, and only when there is a contact email on the job. If there
            is not, the ticket stays flagged as unsent so somebody can deal with it — nothing is
            skipped silently.
          </p>

          {canEdit && (
            <div className="mt-4 flex items-center gap-3">
              <Button variant="primary" onClick={save} disabled={saving || !dirty}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              {dirty && <span className="text-xs text-amber-700">Unsaved changes</span>}
            </div>
          )}
          {!canEdit && (
            <p className="mt-4 text-xs text-gray-500">
              You need administrator access to change these.
            </p>
          )}
        </>
      )}
    </div>
  )
}
